import { useCallback, useState } from "react";

import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentRailsService } from "../services/paymentRails.service";
import type { ClearRailSettingScope, UpsertRailSettingPayload } from "../types/paymentRails.types";

export interface UseRailMutationsResult {
  /** True while a write is in flight — the owning dialog must block closing and double-submits. */
  isSubmitting: boolean;
  /** Normalized user-facing message of the last failed mutation; cleared on every new attempt. */
  error: string | null;
  clearError: () => void;
  /**
   * `PUT /internal/payments/rails/:rail`. Resolves null on success, or the normalized error message —
   * returned (not only stored in `error`) so a caller can toast it right after the await, where the state
   * value would still be stale.
   */
  setRailState: (rail: string, payload: UpsertRailSettingPayload) => Promise<string | null>;
  /** `DELETE /internal/payments/rails/:rail`. A 404 counts as done — the row was already gone. */
  clearRailScope: (rail: string, scope: ClearRailSettingScope) => Promise<string | null>;
}

/**
 * The rails write path. Owns the HTTP calls, the in-flight flag and the error normalization so the dialogs
 * stay presentational. Every successful write also drops the shared corridors memo, so payins, wallets and
 * the dispersion wizard reflect the flip without a reload.
 */
export function useRailMutations(): UseRailMutationsResult {
  const { t } = useTranslations("paymentRails");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalize = useCallback(
    (caught: unknown): string =>
      HttpApiService.isAccessDeniedError(caught)
        ? t("error_no_write_permission")
        : HttpApiService.getErrorMessage(caught) || t("error_save"),
    [t],
  );

  const setRailState = useCallback(
    async (rail: string, payload: UpsertRailSettingPayload): Promise<string | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await PaymentRailsService.upsert(rail, payload);
        invalidateCorridors();
        return null;
      } catch (caught: unknown) {
        const message = normalize(caught);
        setError(message);
        return message;
      } finally {
        setIsSubmitting(false);
      }
    },
    [normalize],
  );

  const clearRailScope = useCallback(
    async (rail: string, scope: ClearRailSettingScope): Promise<string | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await PaymentRailsService.clear(rail, scope);
        invalidateCorridors();
        return null;
      } catch (caught: unknown) {
        if (HttpApiService.isNotFoundError(caught)) {
          invalidateCorridors();
          return null;
        }
        const message = normalize(caught);
        setError(message);
        return message;
      } finally {
        setIsSubmitting(false);
      }
    },
    [normalize],
  );

  return {
    isSubmitting,
    error,
    clearError: useCallback(() => setError(null), []),
    setRailState,
    clearRailScope,
  };
}
